import { createSelector, createSlice, PayloadAction } from "@reduxjs/toolkit";
import { createCachedSelector } from 're-reselect';
import toolIndex from "toolConstants";
import { containerReducers } from "./container";
import { getPlotProps } from "reduxModules/selectors/containerSelectors";
import { gradients } from "utilities/color";
import { getStoreProp } from "utilities/string";
import { keys, pick, values, has, isEmpty, difference } from "lodash";
import { 
  UIState, 
  BoundingBox, 
  SVGBoundingBox, 
  SetParamsPayload, 
  SetErrorPayload, 
  SetInfoPayload, 
  SetTabPayload 
} from "types/redux/ui";
import { ProjectData, RootState } from "types/redux/common";
import { 
  UIActionTypes,
  ClearModalsAction
} from "types/redux/actions";

/**
 * Initial state for the UI slice
 */
const initialState: UIState = {
  tool: '',
  savekey: '',
  revision: '',
  requested: 'latestrevision',
  isLatest: true,
  reload: null,
  units: 'feet',

  // Modals
  modals: [],
  isModalOpen: false, // Changed from true to false - modals should be closed by default
  modalForward: true,
  modalReopen: null,

  // Tabs
  tabs: [],
  activeTab: '',
  currentView: 'floorplan',
  currentDevice: '',
  zoom: false,
  showing: 'container',

  // Area nav control 
  allowAddArea: true,
  addAnotherArea: true,
  exitToRoom: false,

  // Drawing stuff
  svgBoundingBox: { top: 0, left: 0 },
  scale: 1,
  boundingBox: { width: 0, height: 0, x0: 0, y0: 0, x1: 0, y1: 0, cx: 0, cy: 0 },
  overlaping: {},

  // Plot definition
  showMap: '',
  showTable: 'summary',

  // mouse action
  action: '',
  bubbleContent: false,

  // Click-to-place buffer for the "+" → canvas-click → dialog flow.
  // When the user clicks "+" then clicks the canvas without yet having picked an expansion
  // type, the click location is parked here and the `expansionMicAdd` dialog opens. On
  // confirm, the dialog reads + clears this field and places the mic at the stored location.
  // null when not in a click-to-place flow.
  pendingExpansionMicLocation: null as { x: number; y: number } | null,

  // Parameter-change confirm queue. When the user changes dimensions / ceiling height /
  // acoustics / table while having manual edits, the middleware parks the original action
  // here and opens the `confirmManualEditReset` modal instead of applying it. On confirm,
  // the modal re-dispatches the stored action with a `_confirmedReset` flag that bypasses
  // the intercept. On cancel, the modal just clears this field (no state change).
  pendingParameterChangeAction: null as { type: string; payload: any } | null,

  // Errors
  error: {},
  info: {},
  errorState: false,
  lastError: '',
  showHeatmap: true,
  showDevices: true,

  // moving device flag
  deviceIsMoving: false,
  selectedDevice: null,

  // aimpoint dragging flag
  aimpointDragging: false,

  apiVersion: null,
  // MODIFIED: Set to false since we only use WalkthroughGuide (not Tutorial Modal)
  // REVERT: Restore original localStorage check if Tutorial Modal is re-enabled
  isFirstTimeUser: false,
  // Original: typeof window !== 'undefined' ? localStorage.getItem('hasCompletedTutorial') !== 'true' : true,

  modalResponse: null,

  // quote flags
  submitRequest: false,

  // Tutorial target
  spotlightTarget: null,
  tutorialConfig: [],
  tutorialReset: null,
  tutorialStep: 0,

  // show/hide device overlaps 
  showOverlaps: false,

  // Track the last device view that was opened (for acoustics legend)
  lastDeviceView: '',

  needsResave: false,

  // Modal reset key to force re-rendering when dimensions are reset
  modalResetKey: 0,

  // HearSpace loading state
  hearSpaceLoading: false,
};

/**
 * UI Slice for managing the application's user interface state.
 */
export const uiSlice = createSlice({
  name: "ui",
  initialState: { ...initialState },
  reducers: {
    /**
     * Sets the current tool.
     * @param state - The current state.
     * @param action - The action object.
     */
    setTool: (state, action: PayloadAction<string>) => {
      state.tool = action.payload;
      // Set exitToRoom to true when initializing a tool (normal operation, not in modal)
      state.exitToRoom = true;
    },
    /**
     * Sets the save key and revision.
     * @param state - The current state.
     * @param action - The action object.
     */
    setParams: (state, action: PayloadAction<SetParamsPayload>) => {
      const { savekey, revision } = action.payload;
      state.savekey = savekey;
      state.revision = revision;
    },
    /**
     * Sets the requested revision.
     * @param state - The current state.
     * @param action - The action object.
     */
    setRequested: (state, action: PayloadAction<string>) => {
      state.requested = action.payload;
    },
    /**
     * Sets whether the current revision is the latest.
     * @param state - The current state.
     * @param action - The action object.
     */
    setIsLatest: (state, action: PayloadAction<boolean>) => {
      state.isLatest = action.payload;
    },
    /**
     * Sets the reload flag based on current state and action.
     * @param state - The current state.
     * @param action - The action object.
     */
    setReload: (state, action: PayloadAction<boolean>) => {
      const reloadFlag = action.payload === false;
      state.reload = (!reloadFlag && !state.isLatest && state.requested.includes('latest'));
    },
    /**
     * Toggles between loading latest and current revision.
     * @param state - The current state.
     */
    setLoadLatest: (state) => {
      if (state.isLatest) {
        state.reload = true;
        state.isLatest = false;
      } else {
        state.reload = false;
        state.isLatest = true;
      }
    },
    /**
     * Sets the units (feet or meters).
     * @param state - The current state.
     * @param action - The action object.
     */
    setUnits: (state, action: PayloadAction<'feet' | 'meters'>) => {
      state.units = action.payload;
    },
    /**
     * Parks a click-to-place location for the "+" → canvas → dialog flow (PIG-307 Flow B).
     * Set when the user clicks the canvas in add-mode before having an expansion type
     * configured; read + cleared by `ExpansionDialog` on confirm to place the mic
     * at the stored coordinates. Pass `null` to clear (e.g. on dialog cancel).
     */
    setPendingExpansionMicLocation: (state, action: PayloadAction<{ x: number; y: number } | null>) => {
      state.pendingExpansionMicLocation = action.payload;
    },
    /**
     * Parks a parameter-change action (setDimensions, setCeilingHeight, etc.) that the
     * middleware intercepted because the user has manual edits. The confirm modal
     * (`confirmManualEditReset`) reads this on user confirmation, re-dispatches the
     * action with a `_confirmedReset: true` flag so the intercept lets it through, and
     * clears this field. On cancel the modal clears it without re-dispatch.
     */
    setPendingParameterChangeAction: (state, action: PayloadAction<{ type: string; payload: any } | null>) => {
      state.pendingParameterChangeAction = action.payload;
    },
    /**
     * Opens a modal.
     * @param state - The current state.
     * @param action - The action object.
     */
    openModal: (state, action: PayloadAction<string>) => {
      const i = state.modals.findIndex(modal => modal.modalName === action.payload);
      if (i > -1) {
        state.modals[i].modalOpen = true;
      } else {
        state.modals.push({
          modalName: action.payload,
          modalOpen: true,
        });
      }
      state.isModalOpen = state.modals.some(modal => modal.modalOpen);
    },
    /**
     * Closes a modal or all modals.
     * @param state - The current state.
     * @param action - The action object.
     */
    closeModal: (state, action: PayloadAction<string | undefined>) => {
      const modals = state.modals.map((modal) => 
        (!action.payload || modal.modalName === action.payload) 
          ? { ...modal, modalOpen: false } 
          : modal
      );
      state.modals = modals;
      state.isModalOpen = state.modals.some(modal => modal.modalOpen);
      state.modalResponse = null;
      state.info = {};
    },
    /**
     * Clears all modals.
     * @param state - The current state.
     */
    clearModals: (state, action: PayloadAction<any>) => {
      state.modals = [];
    },
    /**
     * Sets the modal to reopen.
     * @param state - The current state.
     * @param action - The action object.
     */
    setModalReopen: (state, action: PayloadAction<string | null>) => {
      state.modalReopen = action.payload;
    },
    /**
     * Updates a tab or adds a new one.
     * @param state - The current state.
     * @param action - The action object.
     */
    updateTab: (state, action: PayloadAction<SetTabPayload>) => {
      if (!state.tabs.some(tab => tab.id === action.payload.id)) {
        state.tabs = [...state.tabs, action.payload];
      } else {
        const i = state.tabs.findIndex(tab => tab.id === action.payload.id);
        if (i > -1) state.tabs[i].name = action.payload.name;
      }
    },
    /**
     * Deletes a tab.
     * @param state - The current state.
     * @param action - The action object.
     */
    deleteTab: (state, action: PayloadAction<string>) => {
      state.tabs = state.tabs.filter(tab => tab.id !== action.payload);
    },
    /**
     * Sets the active tab and updates related state.
     * @param state - The current state.
     * @param action - The action object.
     */
    setActiveTab: (state, action: PayloadAction<string>) => {
      state.activeTab = action.payload;
      
      // Get tool context
      const toolConstants = toolIndex[state.tool]?.constants;
      
      if (toolConstants?.tool?.containerName) {
        state.showing = (state.tabs.at(0)?.id === action.payload ? 'container' : 'area');
        state.showTable = (state.tabs.at(0)?.id === action.payload ? 'summary' : state.showTable);
      } else {
        // fixme these should probably be defined when we define the tool
        state.showing = 'area';
        state.zoom = true;
      }
      state.action = 'measure';
    },

    /**
     * Redux reducer that toggles heatmap visibility.
     * @param state - Current Redux state
     * @param action - Redux action object
     */
    setShowHeatmap: (state, action: PayloadAction<boolean>) => {
      state.showHeatmap = action.payload;
    },

    /**
     * Redux reducer that toggles device visibility.
     * @param state - Current Redux state
     * @param action - Redux action object
     */
    setShowDevices: (state, action: PayloadAction<boolean>) => {
      state.showDevices = action.payload;
    },

    /**
     * Sets the current view and updates related state.
     * @param state - The current state.
     * @param action - The action object.
     * 
     * @description
     * This reducer updates the current view and performs the following actions:
     * 1. Updates the current device based on the new view:
     *    - If the new view is in the devices list, it becomes the current device.
     *    - If the new view is in the hideDevices list, the current device is set to null.
     *    - Otherwise, the current device remains unchanged.
     * 2. Sets the current view to the new view.
     * 3. Updates the showMap state based on the current device:
     *    - If there's a current device, sets showMap to the default plot for that device.
     *    - If there's no current device, sets showMap to null.
     * 4. Sets the action state to 'measure'.
     */
    setCurrentView: (state, action: PayloadAction<string>) => {
      // iterate through menus and set the current device based on the view
      const toolConstants = toolIndex[state.tool]?.constants;
      const { devices = [], hideDevices = [] } = toolConstants || {};

      let currentDevice = devices.includes(action.payload)
        ? action.payload
        : hideDevices.includes(action.payload)
          ? null
          : state.currentDevice;

      currentDevice = currentDevice === 'camera'
        ? 'cameras'
        : currentDevice;

      // Track the last device view that was opened for acoustics legend
      if (['microphones', 'speakers', 'camera'].includes(action.payload)) {
        state.lastDeviceView = action.payload;
      }

      // For acoustics view, ensure we have a valid currentDevice since acoustics needs one
      // For other hideDevices views, clear currentDevice if coming from a non-device menu
      const nonDeviceMenus = [...hideDevices.filter(view => view !== 'acoustics'), 'equipment_list', 'equipmentlist'];
      if (hideDevices.includes(action.payload) && action.payload !== 'acoustics' && nonDeviceMenus.includes(state.currentView)) {
        currentDevice = null;
      } else if (action.payload === 'acoustics') {
        // For acoustics, ensure we have a valid device
        // If coming from cameras view (showMap is cameraCoverage), keep cameras to preserve camera plot
        if (state.showMap === 'cameraCoverage') {
          currentDevice = 'cameras';
        } else if (!currentDevice || !['microphones', 'speakers'].includes(currentDevice)) {
          // Prioritize speakers for acoustics view, or use lastDeviceView if it's microphones/speakers
          let fallbackDevice = 'speakers'; // Default to speakers for acoustics
          if (state.lastDeviceView === 'microphones' || state.lastDeviceView === 'speakers') {
            fallbackDevice = state.lastDeviceView;
          }
          currentDevice = fallbackDevice;
        }
      }

      state.currentDevice = currentDevice;

      // For acoustics view, preserve the existing showMap to keep showing whatever plot was visible
      // For other views, update showMap based on the current device
      if (action.payload === 'acoustics' && state.showMap) {
        // Keep the existing showMap value when switching to acoustics
        // This preserves camera plots when coming from camera view
      } else {
        const defaultPlot = currentDevice && toolConstants?.defaultPlots
          ? toolConstants.defaultPlots[currentDevice]
          : null;

        // For speakers view, ensure we always have a valid speaker plot type
        if (currentDevice === 'speakers') {
          const validSpeakerPlots = ['directSPL', 'totalSPL', 'relativeAverage', 'STI'];
          const currentPlot = state.showMap;

          // If current plot is invalid for speakers, use the default
          if (!currentPlot || !validSpeakerPlots.includes(currentPlot)) {
            state.showMap = defaultPlot || 'relativeAverage';
          }
          // Otherwise keep the current valid speaker plot
        } else {
          // For non-speaker devices, just use the default
          state.showMap = defaultPlot;
        }
      }

      state.currentView = action.payload;
      state.action = 'measure';
    },
    /**
     * Toggles or sets the zoom state.
     * @param state - The current state.
     * @param action - The action object.
     */
    setZoom: (state, action: PayloadAction<boolean | undefined>) => {
      state.zoom = (action.payload === undefined ? !state.zoom : action.payload);
    },
    /**
     * Sets the modal forward flag.
     * @param state - The current state.
     * @param action - The action object.
     */
    modalForward: (state, action: PayloadAction<boolean>) => {
      state.modalForward = action.payload;
    },
    /**
     * Sets the allow add area and add another area flags.
     * @param state - The current state.
     * @param action - The action object.
     */
    setAllowAddArea: (state, action: PayloadAction<boolean>) => {
      state.allowAddArea = action.payload;
      state.addAnotherArea = action.payload;
    },
    /**
     * Toggles the add another area flag.
     * @param state - The current state.
     */
    setAddAnotherArea: (state) => {
      state.addAnotherArea = !state.addAnotherArea;
    },
    /**
     * Sets the exit to room flag.
     * @param state - The current state.
     * @param action - The action object.
     */
    setExitToRoom: (state, action: PayloadAction<boolean>) => {
      state.exitToRoom = action.payload;
    },
    /**
     * Sets the SVG bounding box.
     * @param state - The current state.
     * @param action - The action object.
     */
    setSvgBoundingBox: (state, action: PayloadAction<SVGBoundingBox>) => {
      state.svgBoundingBox = action.payload;
    },
    /**
     * Sets the scale.
     * @param state - The current state.
     * @param action - The action object.
     */
    setScale: (state, action: PayloadAction<number>) => {
      state.scale = action.payload;
    },
    /**
     * Sets the bounding box.
     * @param state - The current state.
     * @param action - The action object.
     */
    setBoundingBox: (state, action: PayloadAction<BoundingBox>) => {
      state.boundingBox = action.payload;
    },
    /**
     * Sets the overlapping state.
     * @param state - The current state.
     * @param action - The action object.
     */
    setOverlaping: (state, action: PayloadAction<Record<string, any>>) => {
      state.overlaping = action.payload;
    },
    /**
     * Sets the show map state with validation for speaker plots.
     * @param state - The current state.
     * @param action - The action object.
     */
    setShowMap: (state, action: PayloadAction<string>) => {
      const newPlotType = action.payload;

      // Validate plot type for speakers
      if (state.currentDevice === 'speakers') {
        const validSpeakerPlots = ['directSPL', 'totalSPL', 'relativeAverage', 'STI'];

        // Only set the plot type if it's valid for speakers
        if (validSpeakerPlots.includes(newPlotType)) {
          state.showMap = newPlotType;
        } else {
          // Invalid plot type for speakers, use default
          state.showMap = 'relativeAverage';
        }
      } else {
        // For non-speaker devices, accept any plot type
        state.showMap = newPlotType;
      }
    },
    /**
     * Sets the show table state.
     * @param state - The current state.
     * @param action - The action object.
     */
    setShowTable: (state, action: PayloadAction<string>) => {
      state.showTable = action.payload;
    },
    /**
     * Sets the action state and updates showMap for actions.
     * @param state - The current state.
     * @param action - The action object.
     */
    setAction: (state, action: PayloadAction<string>) => {
      state.action = action.payload;
      
      // Position action should trigger position functionality
      if (action.payload === 'position') {
        // Position icon should trigger position functionality for all devices
        state.action = 'position';
      }
      
      // Level action should trigger different functionality based on device
      if (action.payload === 'level') {
        if (state.currentDevice === 'microphones') {
          // For microphones: level icon (horizontal arrows) should trigger distance functionality
          state.action = 'distance';
          state.showMap = 'micDistance';
          state.showHeatmap = true; // Ensure heatmap is visible
        } else if (state.currentDevice === 'cameras') {
          // For cameras: level icon (horizontal arrows) should trigger distance functionality
          state.action = 'distance';
          state.showMap = 'cameraCoverage';
          state.showHeatmap = true; // Ensure heatmap is visible
        }
        // For other devices: level remains as 'level'
      }
      
      // Distance icon should trigger distance functionality
      if (action.payload === 'distance') {
        state.action = 'distance';
        // Set appropriate show map for distance functionality
        if (state.currentDevice === 'microphones') {
          state.showMap = 'micDistance';
          state.showHeatmap = true; // Ensure heatmap is visible
        } else if (state.currentDevice === 'cameras') {
          state.showMap = 'cameraCoverage';
          state.showHeatmap = true; // Ensure heatmap is visible
        }
      }
      
      // Relativelevel icon (5-bar equalizer) should trigger relative average level functionality
      if (action.payload === 'relativelevel') {
        if (state.currentDevice === 'speakers') {
          // Set to 'level' action for mouse handling, but with relativeAverage plot
          state.action = 'level';
          state.showMap = 'relativeAverage';
        }
      }
    },
    /**
     * Sets the bubble content flag.
     * @param state - The current state.
     * @param action - The action object.
     */
    setBubbleContent: (state, action: PayloadAction<boolean>) => {
      state.bubbleContent = action.payload;
    },
    /**
     * Sets the modal response.
     * @param state - The current state.
     * @param action - The action object.
     */
    setModalResponse: (state, action: PayloadAction<any>) => {
      state.modalResponse = action.payload;
    },
    /**
     * Sets or clears an error.
     * @param state - The current state.
     * @param action - The action object.
     */
    setError: (state, action: PayloadAction<SetErrorPayload>) => {
      const { code, msg = null, params = [], reset = false } = action.payload;
      
      if (msg) {
        state.error[code] = {
          code, // unused but prevent drawing?
          params, // Parameter to highlight
          msg,
          reset // clear the error when closing the dialog
        };
      } else {
        delete state.error[code];
      }
      
      const errorCount = keys(state.error).length;
      state.errorState = errorCount > 0;
      state.lastError = errorCount > 0 ? code : '';
      
      if (errorCount === 0) {
        state.modals = state.modals.filter(mod => mod.modalName !== 'warning');
      }
    },
    /**
     * Sets or clears info messages.
     * @param state - The current state.
     * @param action - The action object.
     */
    setInfo: (state, action: PayloadAction<SetInfoPayload | undefined>) => {
      const { code = null, msg = null } = action.payload || {};
      
      if (msg && code) {
        state.info[code] = {
          code,
          msg,
        };
      } else if (code) {
        delete state.info[code];
      } else {
        state.info = {};
      }
    },
    /**
     * Closes warning and resets errors.
     * @param state - The current state.
     */
    closeWarning: (state) => {
      // Reset self resetting errors 
      const keysToDelete = keys(state.error).filter(er => state.error[er].reset);
      
      if (keysToDelete.length > 0) {
        keysToDelete.forEach(key => delete state.error[key]);
        state.action = 'measure';
        
        // DISABLED: Auto-reset behavior for dimension and ceiling height errors
        // This was causing dimensions to incorrectly reset when entering valid values like 44'x36'
        // The validation middleware should handle invalid dimensions, not force a reset to defaults
        //
        // const hasDimensionError = keysToDelete.includes('1000');
        // if (hasDimensionError) {
        //   state.resetDimensionsToDefault = true;
        // }
        // const hasCeilingHeightError = keysToDelete.includes('1010');
        // if (hasCeilingHeightError) {
        //   state.resetCeilingHeightToDefault = true;
        // }
      }
      
      const errorCount = keys(state.error).length;
      state.errorState = errorCount > 0;
      state.lastError = errorCount > 0 ? state.lastError : '';
      
      if (errorCount === 0) {
        state.modals = state.modals.filter(mod => mod.modalName !== 'warning');
      }
    },

    /**
     * Clears the resetDimensionsToDefault flag after it's been processed.
     * @param state - The current state.
     */
    clearResetDimensionsFlag: (state) => {
      state.resetDimensionsToDefault = false;
      // Increment modalResetKey to force modal components to re-render
      state.modalResetKey += 1;
    },
    /**
     * Clears the resetCeilingHeightToDefault flag after it's been processed.
     * @param state - The current state.
     */
    clearResetCeilingHeightFlag: (state) => {
      state.resetCeilingHeightToDefault = false;
      // Increment modalResetKey to force modal components to re-render
      state.modalResetKey += 1;
    },
    /**
     * Sets initial project data and updates related state.
     * @param state - The current state.
     * @param action - The action object.
     */
    setInitProjectData: (state, action: PayloadAction<ProjectData>) => {
      // Update state with payload properties
      keys(state).forEach(key => {
        if (key in action.payload) {
          (state as any)[key] = action.payload[key as keyof ProjectData];
        }
      });
      
      const { areas } = action.payload;
      const areaCount = values(areas).length;
      
      // Get constants from tool context
      const toolConstants = toolIndex[state.tool]?.constants;
      
      if (toolConstants) {
        // Handle potential undefined properties with default values
        const tool = toolConstants.tool || {};
        const { maximumAreas, landingMenu } = tool;
        const { defaultPlots } = toolConstants;
        
        if (typeof maximumAreas === 'number') {
          state.allowAddArea = areaCount < maximumAreas;
        }
        
        if (landingMenu) {
          state.currentView = landingMenu;
          state.currentDevice = landingMenu;
        }
        
        if (defaultPlots && landingMenu) {
          state.showMap = defaultPlots[landingMenu];
        }
      }
      
      state.exitToRoom = true;
      state.isLatest = !!action.payload.latestrevision;
    },
    /**
     * Sets the first-time user flag.
     * @param state - The current state.
     * @param action - The action object.
     */
    setIsFirstTimeUser: (state, action: PayloadAction<boolean>) => {
      state.isFirstTimeUser = action.payload;
    },
    /**
     * Sets the isMoving flag.
     * @param state - The current state.
     * @param action - The action object.
     */
    setDeviceIsMoving: (state, action: PayloadAction<boolean>) => {
      state.deviceIsMoving = action.payload;
    },
    /**
     * Sets the aimpoint dragging flag.
     * @param state - The current state.
     * @param action - The action object.
     */
    setAimpointDragging: (state, action: PayloadAction<boolean>) => {
      state.aimpointDragging = action.payload;
    },
    /**
     * Sets the selectedDevice.
     * @param state - The current state.
     * @param action - The action object.
     */
    setSelectedDevice: (state, action: PayloadAction<string | null>) => {
      state.selectedDevice = action.payload;
    },
    /**
     * Sets the flag to submit the request.
     * @param state - The current state.
     * @param action - The action object.
     */
    setSubmitRequest: (state, action: PayloadAction<boolean>) => {
      state.submitRequest = action.payload;
    },
    /**
     * Sets the target for the tutorial spotlight.
     * @param state - The current state.
     * @param action - The action object.
     */
    setSpotlightTarget: (state, action: PayloadAction<string | null>) => {
      state.spotlightTarget = action.payload;
    },
    /**
     * Sets the configuration for the tutorial.
     * @param state - The current state.
     * @param action - The action object.
     */
    setTutorialConfig: (state, action: PayloadAction<any[]>) => {
      state.tutorialConfig = action.payload;
    },
    /**
     * Sets the parameters to be reset after the tutorial closes.
     * @param state - The current state.
     * @param action - The action object.
     */
    setTutorialReset: (state, action: PayloadAction<Record<string, any>>) => {
      state.tutorialReset = { ...state.tutorialReset, ...action.payload };
    },
    /**
     * Sets the current tutorial step.
     * @param state - The current state.
     * @param action - The action containing the step number.
     */
    setTutorialStep: (state, action: PayloadAction<number>) => {
      state.tutorialStep = action.payload;
    },
    /**
     * Sets the flag to show or hide device overlaps.
     * @param state - The current state.
     * @param action - The action object.
     */
    setShowOverlaps: (state, action: PayloadAction<boolean>) => {
      state.showOverlaps = action.payload;
    },
    /**
     * Sets whether the project needs to be resaved.
     * @param state - The current state.
     * @param action - The action object.
     */
    setNeedsResave: (state, action: PayloadAction<boolean>) => {
      state.needsResave = action.payload;
    },
    /**
     * Sets the HearSpace loading state.
     * @param state - The current state.
     * @param action - The action object.
     */
    setHearSpaceLoading: (state, action: PayloadAction<boolean>) => {
      state.hearSpaceLoading = action.payload;
    },

    // Handle container/setName action (previously in extraReducers)
    handleContainerSetName: (state, action: PayloadAction<{ name: string }>) => {
      // Update tab with current name 
      uiSlice.actions.updateTab({
        id: state.activeTab,
        name: action.payload.name
      });
    },

    // Handle container/addArea action (previously in extraReducers)
    handleContainerAddArea: (state) => {
      state.zoom = false;
    },

    // Handle container/setSpeakerLayout action (previously in extraReducers)
    handleContainerSetSpeakerLayout: (state, action: PayloadAction<any[]>) => {
      if (action.payload && action.payload.length > 0) {
        state.apiVersion = action.payload[action.payload.length - 1].ApiVersion;
        state.showMap = 'directSPL';
      }
    },

    // Handle container/setProjectData action (previously in extraReducers)
    handleContainerSetProjectData: (state, action: PayloadAction<ProjectData>) => {
      // Get tool constants
      const toolConstants = toolIndex[state.tool]?.constants;
      const containerTab = toolConstants?.tool?.containerName;
      
      // Clean up tabs first
      const containerTabId = containerTab ? `_${containerTab.toLowerCase()}` : '';
      const areaKeys = keys(action.payload.areas);
      const allValidTabIds = [containerTabId, ...areaKeys].filter(Boolean);
      
      const tabsToDelete = difference(
        state.tabs.map(tab => tab.id), 
        allValidTabIds
      );
      
      // Delete tabs that are no longer valid
      tabsToDelete.forEach(tabId => {
        uiSlice.actions.deleteTab(tabId);
      });
      
      // Add new tabs
      values(action.payload.areas).forEach(area => {
        const area_ = area as any;
        uiSlice.actions.updateTab({
          id: area_.id,
          name: area_.name
        });
      });

      // Check if current tab is valid; if not, set it
      if (!state.tabs.map(tab => tab.id).includes(state.activeTab) && state.tabs.length > 0) {
        uiSlice.actions.setActiveTab(state.tabs[0].id);
      }
      
      // Initialize project data (using our reducer directly)
      uiSlice.caseReducers.setInitProjectData(state, action);
      
      // Ensure valid device and plot state to prevent runtime errors
      const { tool, devices = [] } = toolConstants || {};
      const { landingMenu, defaultPlots, localCalc } = tool || {};

      // Preserve existing showMap if it's valid for the current device
      // Only reset to default if showMap is empty or invalid
      const hasValidShowMap = state.showMap && state.showMap !== '' && state.showMap !== 'null';

      // Set currentDevice to a safe default if not already set or invalid
      if (localCalc && landingMenu) {
        // Use landingMenu for local calculation tools
        const safeDevice = landingMenu === 'camera' ? 'cameras' : landingMenu;
        state.currentDevice = safeDevice;
        state.currentView = landingMenu;

        // Only set showMap if it doesn't already have a valid value
        if (!hasValidShowMap && defaultPlots && defaultPlots[landingMenu]) {
          state.showMap = defaultPlots[landingMenu];
        }
      } else if (devices.length > 0) {
        // Fallback to first available device
        const firstDevice = devices[0] === 'camera' ? 'cameras' : devices[0];
        state.currentDevice = firstDevice;
        state.currentView = devices[0];

        // Only set showMap if it doesn't already have a valid value
        if (!hasValidShowMap) {
          if (defaultPlots && defaultPlots[devices[0]]) {
            state.showMap = defaultPlots[devices[0]];
          } else {
            // Final fallback to 'directSPL' if available
            state.showMap = 'directSPL';
          }
        }
      } else {
        // Clear device state if no devices available
        state.currentDevice = '';
        state.showMap = '';
        state.currentView = 'floorplan';
      }
      
      // Ensure action is set to a safe default
      state.action = 'measure';
    },

    // Handle container/setCustomized action (previously in extraReducers)
    handleContainerSetCustomized: (state, action: PayloadAction<boolean>) => {
      if (!action.payload) {
        state.action = 'measure';
      }
    },
  },
  
  // No extraReducers for now
  extraReducers: () => {}
});

export const uiReducers = { ...uiSlice.actions };
export default uiSlice.reducer;

// UI Selectors
/**
 * Selector to check if the units are in meters.
 * @param state - The Redux state.
 * @returns True if units are in meters, false otherwise.
 */
export const getIsMeter = (state: RootState): boolean => state.ui.units === 'meters';

/**
 * Selector to get the bounding box.
 * @param state - The Redux state.
 * @returns The bounding box object.
 */
export const getBoundingBox = (state: RootState): BoundingBox => state.ui.boundingBox;

/**
 * Selector to get the scale.
 * @param state - The Redux state.
 * @returns The scale value.
 */
export const getScale = (state: RootState): number => state.ui.scale;

/**
 * Selector to get the SVG bounding box.
 * @param state - The Redux state.
 * @returns The SVG bounding box object.
 */
export const getSVGBoundingBox = (state: RootState): SVGBoundingBox => state.ui.svgBoundingBox;

/**
 * Selector to get the zoom state.
 * @param state - The Redux state.
 * @returns The zoom state.
 */
export const getZoom = (state: RootState): boolean => state.ui.zoom;

/**
 * Selector to get the error state.
 * @param state - The Redux state.
 * @returns The error state.
 */
export const getErrorState = (state: RootState): boolean => state.ui.errorState;

/**
 * Selector to get the reload state.
 * @param state - The Redux state.
 * @returns The reload state.
 */
export const getReload = (state: RootState): boolean | null => state.ui.reload;

/**
 * Selects the current view from the UI state.
 * @function
 * @param state - The Redux state.
 * @returns The current view identifier.
 */
export const getCurrentView = (state: RootState): string => state.ui.currentView;
export const getLastDeviceView = (state: RootState): string => state.ui.lastDeviceView;

/**
 * Selects the current device from the UI state.
 * @function
 * @param state - The Redux state.
 * @returns The current device identifier.
 */
export const getCurrentDevice = (state: RootState): string => state.ui.currentDevice;

/**
 * Interface for the drawing box result
 */
interface DrawingBoxResult {
  bbox: BoundingBox;
  scale: number;
  svgbox: SVGBoundingBox;
  zoom: boolean;
  iconAdj: number;
}

/**
 * Selector to get the drawing box properties.
 * @param state - The Redux state.
 * @returns An object containing drawing box properties.
 */
export const getDrawingBox = createSelector(
  getBoundingBox, 
  getScale, 
  getSVGBoundingBox, 
  getZoom,
  (state: RootState) => toolIndex[state.ui.tool]?.constants?.iconMultiplier || 1,
  (bbox, scale, svgbox, zoom, iconAdj): DrawingBoxResult => ({
    bbox,
    scale,
    svgbox,
    zoom,
    iconAdj
  })
);

/**
 * Selector to get the color gradient based on plot properties.
 * @param state - The Redux state.
 * @returns The color gradient object.
 */
export const getColorGradient = createSelector(
  (state: RootState) => getPlotProps(state as any),
  (state: RootState) => {
    // Get theme mode from localStorage
    const themeMode = localStorage.getItem('theme-mode') || 'light';
    return themeMode;
  },
  (props, themeMode) => {
    const { plotType: type, ...properties } = props as any;
    if (has(gradients, type)) {
      // Pass theme mode to gradient functions
      return (gradients as any)[type]({ ...properties, themeMode });
    }
    return undefined;
  }
);

/**
 * Interface for selector arguments
 */
interface SelectorArgs {
  errorCode?: string;
  name?: string;
  [key: string]: any;
}

/**
 * Cached selector to get a UI property.
 * @param state - The Redux state.
 * @param property - The property to retrieve.
 * @returns The value of the requested UI property.
 */
export const getUIProperty = createCachedSelector(
  (state: RootState) => state.ui,
  (state: RootState, property: string) => property,
  (uiState, property) => uiState?.[getStoreProp(property) as keyof UIState]
)(
  (state, property) => property
);

/**
 * Cached selector to check if a specific error exists.
 * @param state - The Redux state.
 * @param args - The arguments object.
 * @returns True if the error exists, false otherwise.
 */
export const isInError = createCachedSelector(
  (state: RootState) => values(state.ui.error),
  (state: RootState, args: SelectorArgs) => pick(args, ['errorCode', 'name']),
  (errors, args) => {
    const { errorCode, name = null } = args;
    return errors.some(error => error.code === errorCode && (name ? error.params.includes(name) : true));
  }
)(
  (state, args) => {
    const { errorCode = '0', name = '0' } = args;
    return `${errorCode}:${name}`;
  }
);

/**
 * Selector to get the HearSpace loading state.
 * @param state - The Redux state.
 * @returns True if HearSpace is loading, false otherwise.
 */
export const getHearSpaceLoading = (state: RootState): boolean => state.ui.hearSpaceLoading;

/**
 * Selector to determine if the map should be shown.
 * @function
 * @param state - The Redux state.
 * @returns True if the map should be shown, false otherwise.
 *
 * @description
 * This selector combines multiple pieces of state to determine if the map should be displayed.
 * It returns true if all the following conditions are met:
 * 1. There is no error state.
 * 2. The current view is one of: 'acoustics', 'designpriority', 'speakers', 'microphones', 'camera'.
 * 3. A current device is selected.
 * 4. The application is not in a modal state (exitToRoom is true).
 * 5. No device is currently being moved.
 * 6. Heatmap display is enabled.
 */
export const getShowMap = createSelector(
  getErrorState,
  getCurrentView,
  getCurrentDevice,
  getBoundingBox,
  (state: RootState) => state.ui.exitToRoom,
  (state: RootState) => state.ui.deviceIsMoving,
  (state: RootState) => state.ui?.showHeatmap,
  (error, currentView, currentDevice, box, noModal, deviceIsMoving = false, showmap = true) => {
    return !error &&
      ['acoustics', 'designpriority', 'speakers', 'microphones', 'camera'].includes(currentView) &&
      currentDevice &&
      !isEmpty(box) &&
      noModal &&
      !deviceIsMoving &&
      showmap;
  }
);
